import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/axios';
import { useToast } from '../hooks/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Bot, Save, Loader2, Settings2, Zap, AlertCircle } from 'lucide-react';

export default function Settings() {
  const { toast } = useToast();
  const [aiEnabled, setAiEnabled] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 1. Memorized Fetch Function
  const fetchUserSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      if (data.success) {
        // Convert SQLite integer (1/0) to boolean
        setAiEnabled(data.user.ai_bot_enabled === 1);
        setSystemPrompt(data.user.ai_system_prompt || '');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load settings.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // 2. Lifecycle Hook
  useEffect(() => {
    fetchUserSettings();
  }, [fetchUserSettings]);

  // 3. Toggle AI Status
  const handleToggleAi = async (checked) => {
    // Optimistic UI update for immediate feedback
    setAiEnabled(checked);
    try {
      const { data } = await api.post('/ai/toggle', { enabled: checked }); 
      if (data.success) {
        toast({ 
          title: checked ? 'AI Activated' : 'AI Deactivated', 
          description: data.message 
        });
      }
    } catch (error) {
      // Revert if API fails
      setAiEnabled(!checked);
      toast({
        title: 'Update Failed',
        description: 'Could not toggle AI status.',
        variant: 'destructive'
      });
    }
  };

  // 4. Save Prompt
  const handleSavePrompt = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { data } = await api.put('/ai/prompt', { prompt: systemPrompt }); 
      if (data.success) {
        toast({ title: 'Success', description: 'AI System Prompt saved successfully.' });
      }
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error.response?.data?.error || 'Could not save prompt.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 h-[50vh] flex flex-col items-center justify-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
        <p>Loading AI Configurations...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-8">
      
      {/* Header */}
      <div className="flex items-center gap-3 border-b pb-6">
        <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
            <Settings2 className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Platform Settings</h1>
          <p className="text-muted-foreground mt-1">Configure your automated WhatsApp AI assistant.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        
        {/* Left Column: Info & Status */}
        <div className="col-span-1 space-y-6">
          <Card className={`border-t-4 shadow-sm ${aiEnabled ? 'border-t-green-500' : 'border-t-gray-300'}`}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className={aiEnabled ? "text-green-600" : "text-gray-400"} /> 
                    AI Auto-Reply
                  </CardTitle>
                </div>
                <Switch 
                  checked={aiEnabled} 
                  onCheckedChange={handleToggleAi} 
                  className={aiEnabled ? "bg-green-500" : ""}
                />
              </div>
              <CardDescription className="pt-2">
                Allow the Whatsappbot AI engine to autonomously read and reply to incoming WhatsApp messages.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50/50 border border-blue-100 text-blue-800 p-4 rounded-lg text-sm flex gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p>
                  <strong>Billing Note:</strong> Each AI reply consumes tokens and deducts from your wallet balance. Ensure you have sufficient funds to keep the bot running.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: AI Configuration */}
        <div className="col-span-1 md:col-span-2 space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="bg-gray-50/50 border-b">
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" /> System Instructions
              </CardTitle>
              <CardDescription>
                Give your AI a distinct personality and strict rules on how to handle inquiries. 
                This acts as the "brain" for your bot.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSavePrompt} className="space-y-5">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="prompt" className="text-gray-700 font-semibold">Base Prompt</Label>
                    <span className="text-xs text-gray-400 font-mono">Max ~500 tokens</span>
                  </div>
                  <Textarea 
                    id="prompt"
                    value={systemPrompt} 
                    onChange={(e) => setSystemPrompt(e.target.value)} 
                    placeholder="You are a friendly and professional customer support agent for Yashi Associates. Always greet the user warmly and keep your answers under 3 sentences..." 
                    rows={8}
                    className="bg-white font-mono text-sm leading-relaxed" 
                    disabled={!aiEnabled}
                  />
                  {!aiEnabled && (
                    <p className="text-xs text-amber-600 font-medium mt-1">
                      Enable AI Auto-Reply to edit and apply these instructions.
                    </p>
                  )}
                </div>

                <div className="flex justify-end pt-2">
                  <Button 
                    type="submit" 
                    disabled={saving || !aiEnabled} 
                    className="bg-indigo-600 hover:bg-indigo-700 px-8"
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Configuration
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
}